// src/pages/ForgotPassword/ForgotPasswordPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FaArrowRight,
  FaCheck,
  FaEnvelope,
  FaInfoCircle,
  FaPaperPlane,
  FaShieldAlt,
  FaSignInAlt,
  FaTrophy,
} from "react-icons/fa";

import backgroundImage from "./HeaderSlider_02.jpg";
import useScrollToTop from "./../../hooks/useScrollToTop";
import ScrollIndicator from "./../../hooks/ScrollIndicator";
import { forgotPassword } from "./../../services/passwordServices";

const INPUT =
  "w-full rounded-2xl border border-slate-200 bg-white/90 px-4 py-3 text-sm font-medium text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-950/80 dark:text-slate-100 dark:focus:border-indigo-500 dark:focus:ring-indigo-950";

export default function ForgotPasswordPage() {
  useScrollToTop();

  const navigate = useNavigate();
  const location = useLocation();

  const initialEmail = location.state?.email || "";

  const [email, setEmail] = useState(initialEmail);
  const [message, setMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(5);

  const isEmailValid = useMemo(() => {
    return email.trim().includes("@");
  }, [email]);

  useEffect(() => {
    if (!message) return;

    setCountdown(5);

    const intervalId = setInterval(() => {
      setCountdown((current) => {
        if (current <= 1) {
          clearInterval(intervalId);
          navigate("/login", {
            state: {
              email: email.trim().toLowerCase(),
              resetRequested: true,
            },
          });
          return 0;
        }

        return current - 1;
      });
    }, 1000);

    return () => clearInterval(intervalId);
  }, [message, navigate, email]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    setErrorMessage("");
    setMessage("");

    if (!isEmailValid) {
      setErrorMessage("Merci de renseigner une adresse email valide.");
      return;
    }

    try {
      setLoading(true);

      const cleanEmail = email.trim().toLowerCase();
      const result = await forgotPassword(cleanEmail);

      if (result?.success) {
        setMessage(
          "Si un compte avec cet email existe, un lien de réinitialisation a été envoyé."
        );
      } else {
        setErrorMessage(result?.message || "Une erreur est survenue.");
      }
    } catch (error) {
      console.error("Erreur demande de réinitialisation :", error);
      setErrorMessage("Impossible d’envoyer le lien pour le moment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main
      className="relative min-h-screen overflow-hidden bg-slate-950 bg-cover bg-center px-3 py-8 pt-[120px] text-slate-900 dark:text-white sm:px-5 md:pt-[145px]"
      style={{ backgroundImage: `url(${backgroundImage})` }}
    >
      <ScrollIndicator />

      <div className="absolute inset-0 bg-slate-950/70 backdrop-blur-[2px]" />
      <div className="absolute -right-24 top-20 h-72 w-72 rounded-full bg-indigo-500/30 blur-3xl" />
      <div className="absolute bottom-0 left-10 h-72 w-72 rounded-full bg-pink-500/20 blur-3xl" />
      <div className="absolute left-1/2 top-1/2 h-80 w-80 -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-500/10 blur-3xl" />

      <div className="relative mx-auto grid w-full max-w-[1180px] gap-6 lg:grid-cols-[minmax(0,1fr)_460px] lg:items-center">
        <section className="hidden text-white lg:block">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-white/80 backdrop-blur">
            <FaTrophy />
            MonAppliTournoi
          </div>

          <h1 className="mt-5 max-w-3xl text-5xl font-bold tracking-tight">
            Récupérez l’accès à votre espace compétition.
          </h1>

          <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-300">
            Indiquez l’adresse email utilisée lors de votre inscription. Si un
            compte existe, vous recevrez un lien sécurisé pour réinitialiser
            votre mot de passe.
          </p>

          <div className="mt-8 grid max-w-3xl grid-cols-1 gap-3 sm:grid-cols-3">
            <HeroFeature
              icon={FaShieldAlt}
              title="Lien sécurisé"
              text="Réinitialisation via token temporaire."
            />
            <HeroFeature
              icon={FaEnvelope}
              title="Envoi par email"
              text="Vérifiez aussi vos spams."
            />
            <HeroFeature
              icon={FaTrophy}
              title="Retour rapide"
              text="Reprenez la gestion de vos tournois."
            />
          </div>
        </section>

        <section className="mx-auto w-full max-w-xl lg:max-w-none">
          <div className="overflow-hidden rounded-[34px] border border-white/20 bg-white/95 shadow-2xl backdrop-blur-xl dark:border-slate-800 dark:bg-slate-900/95">
            <div className="relative overflow-hidden bg-slate-950 p-6 text-white sm:p-8">
              <div className="absolute -right-14 -top-14 h-40 w-40 rounded-full bg-indigo-500/30 blur-3xl" />
              <div className="absolute -bottom-16 left-1/3 h-40 w-40 rounded-full bg-pink-500/20 blur-3xl" />

              <div className="relative">
                <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10 text-xl">
                  <FaPaperPlane />
                </div>

                <h2 className="text-3xl font-bold tracking-tight">
                  Mot de passe oublié
                </h2>

                <p className="mt-2 text-sm leading-relaxed text-slate-300">
                  Recevez un lien pour définir un nouveau mot de passe.
                </p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5 p-5 sm:p-7">
              {message ? (
                <div
                  className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm font-semibold text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-300"
                  role="alert"
                >
                  <div className="flex items-start gap-3">
                    <FaCheck className="mt-0.5 shrink-0" />
                    <span>
                      {message} Redirection vers la connexion dans{" "}
                      <strong>{countdown}s</strong>…
                    </span>
                  </div>
                </div>
              ) : null}

              {errorMessage ? (
                <div
                  className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-300"
                  role="alert"
                >
                  {errorMessage}
                </div>
              ) : null}

              <Field label="Email" icon={FaEnvelope}>
                <input
                  type="email"
                  placeholder="votre.email@exemple.fr"
                  className={INPUT}
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value);
                    setErrorMessage("");
                    setMessage("");
                  }}
                  autoComplete="email"
                  required
                />
              </Field>

              <button
                type="submit"
                disabled={loading || !isEmailValid || Boolean(message)}
                className="group inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-indigo-600 via-violet-600 to-pink-500 px-4 py-3.5 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0"
              >
                <FaPaperPlane />
                {loading
                  ? "Envoi en cours..."
                  : "Envoyer le lien de réinitialisation"}
                {!loading ? (
                  <FaArrowRight className="transition group-hover:translate-x-1" />
                ) : null}
              </button>

              <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4 text-sm font-semibold text-blue-700 dark:border-blue-900/60 dark:bg-blue-950/40 dark:text-blue-300">
                <div className="flex items-start gap-3">
                  <FaInfoCircle className="mt-0.5 shrink-0" />
                  <span>
                    Pensez à vérifier vos spams si vous ne voyez pas l’email
                    après quelques minutes.
                  </span>
                </div>
              </div>

              <div className="rounded-2xl bg-slate-50 p-4 text-center text-sm text-slate-600 dark:bg-slate-950/60 dark:text-slate-300">
                Vous vous souvenez de votre mot de passe ?{" "}
                <button
                  type="button"
                  onClick={() => navigate("/login")}
                  className="inline-flex items-center gap-1 font-bold text-indigo-600 hover:underline dark:text-indigo-300"
                >
                  <FaSignInAlt className="text-xs" />
                  Se connecter
                </button>
              </div>
            </form>
          </div>
        </section>
      </div>
    </main>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <label className="block">
      <span className="mb-2 flex items-center gap-2 text-sm font-bold text-slate-700 dark:text-slate-200">
        <Icon className="text-indigo-500" />
        {label}
      </span>

      {children}
    </label>
  );
}

function HeroFeature({ icon: Icon, title, text }) {
  return (
    <div className="rounded-[24px] border border-white/10 bg-white/[0.08] p-4 backdrop-blur">
      <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10">
        <Icon />
      </div>

      <h3 className="font-bold text-white">{title}</h3>
      <p className="mt-1 text-sm leading-relaxed text-slate-300">{text}</p>
    </div>
  );
}